import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {Box, useSlotRecipe} from '@chakra-ui/react3'
import DisImage from '../../dis-image'
import PriceProduct from '../../cms/price-product'
import {ChevronRightIcon} from '../../icons'
import {hasValidImageSrc, hasValidPrice} from '../../../utils/utils'
import {navigationParentRecipe} from './index.recipe'
import RichText from '../../cms/rich-text'

/**
 * A navigation parent supporting two visual variants: `link` and `card`.
 *
 * - `link` — a full-width button row with a `title` slot and a chevron that appears on hover.
 * - `card` — a product card with an optional image, `copy`, and price derived from a product object.
 *
 * The variant and everything it carries arrive as the CMS `variant` object, so a
 * level-2 entry is handed to the component as authored. A level-1 parent has no
 * such object — it carries only a title to render, and its own accent flag sits
 * flat on the item, where nothing reads it — so it falls back to the recipe
 * defaults and renders as a plain link row.
 *
 * Active state is controlled externally via the `isActive` prop; the parent component
 * is responsible for tracking which item is selected.
 * The button carries no navigation of its own — the caller wires `onClick` to
 * open the entry's pane, or stands in a not-implemented toast where routing is
 * not yet wired (see the flyout partials).
 *
 * An entry that opens a pane is a disclosure control: `controls` names that
 * pane and `isActive` becomes its expanded state, leaving the caller to decide
 * what "open" means. An entry that opens nothing passes neither.
 *
 * @param {Object} [variant] - The CMS variant object
 * @param {'link'|'card'} [variant.type='link'] - Visual variant
 * @param {boolean|string} [variant.accentColor] - `link` only; renders the title in the brand orange token
 * @param {Object} [variant.image] - `card` only; the card thumbnail (3.75rem × 3.75rem). Its
 *   `src` arrives as a resolved URL — the authored `dynamicimage::` token is substituted before
 *   the payload reaches the client — and stays empty when no image was authored.
 * @param {string} [variant.copy] - `card` only; body copy shown below the title
 * @param {Object} [variant.priceProduct] - `card` only; the price, resolved from its `productId` by SFCC
 * @param {string} title - Primary title text
 * @param {boolean} [isActive] - Active/selected state, controlled by the parent
 * @param {string} [controls] - Id of the pane this entry discloses
 * @param {Function} [onClick] - Click handler; receives the click event
 */
const NavigationParent = ({variant, title, isActive, controls, onClick}) => {
    const {type, accentColor, image, copy, priceProduct} = variant || {}
    const isCard = type === 'card'

    const recipe = useSlotRecipe({recipe: navigationParentRecipe})
    const styles = useMemo(() => recipe({variant: type, accentColor}), [recipe, type, accentColor])

    const priceElement = useMemo(() => {
        // SFCC resolves `value` only for a product it can price, so an absent one means
        // the card authored no price or the product is not priced on this site, while a
        // `0` is a real price and still renders. Dropping the row here rather than leaving
        // it to `PriceProduct`'s own check keeps the empty slot out of the card's grid.
        if (!hasValidPrice(priceProduct?.value)) return null
        return (
            <Box css={styles.price}>
                <PriceProduct {...priceProduct} variant="compact" />
            </Box>
        )
    }, [priceProduct, styles.price])

    return (
        <Box
            as="button"
            type="button"
            className="leap group"
            css={styles.root}
            onClick={onClick}
            aria-expanded={controls ? Boolean(isActive) : undefined}
            aria-controls={controls}
            data-active={isActive ? '' : undefined}
        >
            {isCard && (
                <Box css={styles.imageContainer}>
                    {hasValidImageSrc(image) && (
                        <DisImage
                            src={image.src}
                            // The navigation image carries no alternative text of its
                            // own in the schema, and the title beside it already names
                            // the entry, so it is decorative either way.
                            alt=""
                            objectFit="contain"
                            widths={{base: '60px'}} // matches imageContainer slot width of 3.75rem
                            // one pane serves every level-1 entry, so a new URL here is a different image (STOK-17559)
                            resetOnSrcChange
                        />
                    )}
                </Box>
            )}
            <Box css={styles.content}>
                <RichText css={styles.title}>{title}</RichText>
                {isCard && (
                    <>
                        {copy && <RichText css={styles.copy}>{copy}</RichText>}
                        {priceElement}
                    </>
                )}
            </Box>
            <Box css={styles.chevron} aria-hidden="true">
                <ChevronRightIcon />
            </Box>
        </Box>
    )
}

NavigationParent.propTypes = {
    variant: PropTypes.shape({
        type: PropTypes.oneOf(Object.keys(navigationParentRecipe.variants.variant)),
        // Boolean and string forms both reach the client; the recipe's compound
        // variant lists both shapes, so either one selects the accent styles.
        accentColor: PropTypes.oneOf([true, false, 'true', 'false']),
        image: PropTypes.shape({
            src: PropTypes.string
        }),
        copy: PropTypes.string,
        // The authored `productId`/`label`, plus the four fields SFCC's
        // `calculatePriceProduct` resolves from the product.
        priceProduct: PropTypes.shape({
            productId: PropTypes.string,
            label: PropTypes.string,
            value: PropTypes.number,
            valueListPrice: PropTypes.number,
            hidePromotionSignals: PropTypes.bool,
            currency: PropTypes.string
        })
    }),
    title: PropTypes.string.isRequired,
    isActive: PropTypes.bool,
    controls: PropTypes.string,
    onClick: PropTypes.func
}

export default NavigationParent
